<template>
  <with-loader :id="id" :loading="isLoading" class="w-full">
    <ul ref="unorderedList" class="w-full">
      <li v-for="(item, i) of list"
          :key="i"
          class="flex items-center text-gray-dark pl-3"
          @mouseover="hoverIndex = i"
          @mouseleave="hoverIndex = null"
      >
        <checkbox :id="`checkbox${i}`"
                  :is-checked="isCheckboxChecked(item)"
                  :disabled="isDisabled"
                  :class="isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'"
                  @update:isChecked="value => toggleCheckbox(value, item)"
        />
        <with-loader v-if="mode === EXAMPLE_EDITOR_MODE.EDIT && editIndex === i" id="edit-example-input"
                     :loading="exampleUpdated" class="ml-2 w-full" use-as-overlay>
          <base-input class="w-full"
                      v-model.trim="exampleText"
                      ref="editExampleInput"
                      @keydown.esc="switchModeToDefault"
                      @keydown.enter="updateExample"
                      :disabled="exampleUpdated"
          />
        </with-loader>
        <span v-else
              class="w-full ml-2 pl-2 py-2 border border-solid truncate"
              :class="isHover(i) ? 'border-gray rounded' : 'border-transparent'"
              @click="switchModeToEdit(item, i)">
          {{ item.example }}
        </span>
      </li>
    </ul>
  </with-loader>
</template>

<script>
import assoc from 'ramda/src/assoc'
import head from 'ramda/src/head'
import intentExamplesService from '@/services/amio/intent-examples.service'
import intentModule from '@/store/modules/intent.module'
import WithLoader from '@/components/common/WithLoader'
import Checkbox from '@/components/preview/Checkbox'
import { EXAMPLE_EDITOR_MODE } from '@/store/constants'
import toaster from '@/utils/toaster'
import errorUtils from '@/utils/error.utils'

export default {
  name: 'SelectableList',
  components: { WithLoader, Checkbox },
  props: {
    id: {
      type: String,
      required: true
    },
    botId: {
      type: String,
      required: true
    },
    value: {
      type: Object,
      required: true
    },
    intent: {
      type: Object,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    list: {
      type: [Array, Set],
      default: () => []
    },
    mode: {
      type: Number,
      required: true
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    tooltipText: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      hoverIndex: null,
      editIndex: null,
      exampleText: '',
      exampleUpdated: false,
      EXAMPLE_EDITOR_MODE
      
    }
  },

  methods: {
    isHover(index) {
      return this.hoverIndex === index
    },

    isCheckboxChecked(item) {
      return !!this.value[item.id]
    },

    toggleCheckbox(checkedValue, item) {
      if (checkedValue) {
        this.$set(this.value, item.id, item)
      } else {
        this.$delete(this.value, item.id)
      }
    },

    switchModeToEdit(item, index) {
      this.exampleText = item.example
      this.editIndex = index
      this.$emit('update-mode', EXAMPLE_EDITOR_MODE.EDIT)
      this.$nextTick(() => {
        this.$refs.editExampleInput[0].focus()
      })
    },

    switchModeToDefault() {
      this.$emit('update-mode', EXAMPLE_EDITOR_MODE.DEFAULT)
    },

    async updateExample() {
      try {
        this.exampleUpdated = true
        const editedExample = this.list[this.editIndex]
        const updatedExample = await intentExamplesService.update(editedExample.id, { example: this.exampleText })
        this.$emit('update-example', updatedExample)
        if (editedExample.example === this.intent.example) {
          const updatedIntent = assoc('example', updatedExample.example, this.intent)
          intentModule.setIntent(updatedIntent)
        }
        this.switchModeToDefault()
      } catch (e) {
        this.toasterError(e)
      } finally {
        this.exampleUpdated = false
      }
    },

    toasterError(e) {
      const errorMessage = head(e.response.data.details.errors)?.message
      if (errorMessage.startsWith('example:')) {
        const errorMessageSplit = errorMessage.split('answer:')
        const { content, fullContent } = errorUtils.resolveDuplicateExampleMessage(errorMessageSplit[0])
        const answerId = errorMessageSplit[1].split('answerId: ')[1].replace(')', '')
        const callToAction = `already exists for answer:<br/><a target="_blank" href="/bots/${this.botId}/answers/${answerId}">${errorMessageSplit[1]}</a>`
        toaster.error(content, null, {}, fullContent, callToAction)
        return
      }

      toaster.error(errorMessage)
    }
  }
}
</script>
